// Semprion HQ ▸ OL Finance features — which optional OL Finance features ANY organization may see
// (backend olfin/features.py; GET/PUT /api/olfin/platform/features). Off = hidden for every organization, whatever its
// Treasurer chose. Allowed = each organization's Treasurer may turn it on (organizations start in Simple mode).
// The core books are the same for every customer and are not listed as switches.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OLFinanceFeaturesPane() {
  const [d, setD] = useState(null); const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState(''); const [err, setErr] = useState('');
  const take = (data) => { setD(data); setDraft(Object.fromEntries(data.catalog.map((f) => [f.key, f.allowed]))); };
  const load = useCallback(() => axios.get(`${API_URL}/api/olfin/platform/features`).then(({ data }) => take(data))
    .catch((e) => setErr(e?.response?.data?.detail || 'Could not load OL Finance features')), []);
  useEffect(() => { load(); }, [load]);
  const dirty = d && d.catalog.some((f) => draft[f.key] !== f.allowed);
  const save = async () => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const body = Object.fromEntries(Object.entries(draft).map(([k, v]) => [k, { allowed: v }]));
      const { data } = await axios.put(`${API_URL}/api/olfin/platform/features`, body); take(data);
      setMsg('Saved — every organization follows this within 30 seconds');
    } catch (e) { setErr(e?.response?.data?.detail || 'Save failed'); } finally { setBusy(false); }
  };
  if (!d) return <div className="text-sm text-muted-foreground">{err || <Loader2 className="h-4 w-4 animate-spin" />}</div>;
  const u = d.usage;
  return (
    <div className="space-y-4" data-testid="hq-olfin-features">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">OL Finance features</h2>
          <p className="text-sm text-muted-foreground">Choose the extras organizations may turn on. Off hides a feature for everyone, whatever their Treasurer picked.
            {' '}{u.companies} organizations · {u.simple} in Simple mode · edition {d.edition}.</p>
        </div>
        <Button disabled={!dirty || busy} onClick={save} data-testid="hq-olfin-save">{busy ? 'Saving…' : 'Save'}</Button>
      </div>
      {msg && <p className="text-sm text-emerald-500">{msg}</p>}{err && <p className="text-sm text-red-500">{err}</p>}
      <Card className="bg-card border-border"><CardContent className="p-4">
        <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Core — every organization, always</p>
        <p className="text-sm">{d.core.join(' · ')}</p>
      </CardContent></Card>
      <Card className="bg-card border-border"><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-muted-foreground border-b border-border">
            <th className="p-3">Extra</th><th className="p-3">Organizations may use it</th><th className="p-3 text-right">Turned on by</th></tr></thead>
          <tbody>
            {d.catalog.map((f) => (
              <tr key={f.key} className="border-b border-border/50" data-testid={`hq-olfin-row-${f.key}`}>
                <td className="p-3"><div className="font-medium">{f.label}</div><div className="text-xs text-muted-foreground">{f.help}</div></td>
                <td className="p-3">
                  <div className="inline-flex rounded border border-border overflow-hidden">
                    {[[true, 'Allowed'], [false, 'Off']].map(([v, l]) => (
                      <button key={l} type="button" onClick={() => setDraft({ ...draft, [f.key]: v })} data-testid={`hq-olfin-${f.key}-${v ? 'allowed' : 'off'}`}
                        className={`px-3 py-1 text-xs ${draft[f.key] === v ? (v ? 'bg-emerald-600 text-white' : 'bg-zinc-600 text-white') : 'bg-background text-muted-foreground'}`}>{l}</button>
                    ))}
                  </div>
                  {!f.decided && <span className="ml-2 text-[10px] text-muted-foreground">edition default</span>}
                </td>
                <td className="p-3 text-right tabular-nums">{u.on[f.key]} / {u.companies}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent></Card>
      <p className="text-xs text-muted-foreground">Visibility only: switching a feature off hides its menus and screens; nothing recorded is deleted and every report keeps all transactions.</p>
    </div>
  );
}
